<template>
    <v-dialog :modelValue="modelValue" @update:modelValue="syncVModel" :min-width="800" :width="1000">
        <v-card>
            <v-card-title>
                <h3>Edit Monitor Installation Type</h3>
            </v-card-title>
            <v-card-text>
                <v-container>
                    <v-row>
                        <v-col>
                            <p>
                                Please note that changing the monitor install type impacts the way that monitor readings are leveraged. Inline monitors require
                                a valid pump schedule and will ignore samples when the pump is not running according to the schedule.
                            </p>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <MonitorInstallTypeSelect v-model="updatableInstallType" show-descriptions />
                        </v-col>
                    </v-row>
                    <v-row v-if="updatableInstallType === MonitorInstallTypesV1.Inline">
                        <v-col>
                            <InlineFixtureVersionSelect v-model="updatableFixtureVersion" show-descriptions />
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="gray-darken-1" @click="syncVModel(false)" size="large">Cancel</v-btn>
                <v-btn color="red-darken-1" @click="changeMonitorInstallType" size="large">Save</v-btn>
            </v-card-actions>
            <v-overlay contained :model-value="saving" class="align-center justify-center">
                <v-progress-circular indeterminate size="64"></v-progress-circular>
            </v-overlay>
        </v-card>
    </v-dialog>
</template>

<script lang="ts" setup>
import { handleApiError } from '@/lib/utils'
import { InlineFixtureVersionsV1, MonitorInstallTypesV1 } from '@geckoal/gecko-api-client'
import { useVuelidate, type ValidationArgs } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { computed, reactive, ref, type PropType } from 'vue'
import { useApi } from '../../api'
import InlineFixtureVersionSelect from './InlineFixtureVersionSelect.vue'
import MonitorInstallTypeSelect from './MonitorInstallTypeSelect.vue'

const props = defineProps({
    accountId: {
        type: Number,
        required: true
    },
    vesselId: {
        type: Number,
        required: true
    },
    installType: {
        type: String as PropType<MonitorInstallTypesV1>,
        required: true
    },
    fixtureVersion: {
        type: String as PropType<InlineFixtureVersionsV1>,
        required: false
    },
    modelValue: {
        type: Boolean
    }
})

const updatableInstallType = ref<MonitorInstallTypesV1>(props.installType)
const updatableFixtureVersion = ref<InlineFixtureVersionsV1 | undefined>(props.fixtureVersion)

const saving = ref(false)

const emit = defineEmits<{
    (event: 'changedMonitorType'): void
    (event: 'update:modelValue', value: boolean): void
}>()

const syncVModel = (value: boolean) => {
    emit('update:modelValue', value)
}

interface FormState {
    monitorInstallType: MonitorInstallTypesV1 | undefined
    inlineFixtureVersion: InlineFixtureVersionsV1 | undefined
}

const state = reactive<FormState>({
    monitorInstallType: MonitorInstallTypesV1.Poolside,
    inlineFixtureVersion: undefined
})

const rules = computed(() => {
    const output: ValidationArgs<FormState> = {
        monitorInstallType: { required },
        inlineFixtureVersion: {}
    }
    if (state.monitorInstallType === MonitorInstallTypesV1.Inline) {
        output.inlineFixtureVersion = { required }
    }
    return output
})

const v$ = useVuelidate(rules, state)

const changeMonitorInstallType = async () => {
    const isFormCorrect = await v$.value.$validate()
    if (!isFormCorrect) return

    await handleApiError(
        async () => {
            saving.value = true
            const result = await useApi().updateVesselV1({
                accountId: props.accountId,
                vesselId: props.vesselId,
                postVesselV1: {
                    monitorInstallType: updatableInstallType.value
                }
            })
            console.log('updated monitor install type', result)
            emit('changedMonitorType')
            syncVModel(false)
        },
        async () => {
            saving.value = false
        }
    )
}
</script>

<style lang="scss" scoped></style>
